import React, {useEffect, useRef} from 'react';
import {StyleSheet, View, Animated, Easing} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import { useMediaQuery } from '@/Hooks/useMediaQuery/useMediaQuery';

export interface Level {
  name: string;
  order: number;
  isCurrent: boolean;
  isCompleted: boolean;
  color: string;
  isLocked?: boolean;
}

interface LevelTrackerProps {
  levels: Level[];
}

const LevelTracker: React.FC<LevelTrackerProps> = ({levels}) => {
  // Levels should already be filtered to show only current and next level
  // This is just a safety check
  const displayLevels = levels;

  const {isMobileOrTab} = useMediaQuery();

  // Animation values
  const progressAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const lineWidth = useRef(0);
  const currentTag = (
    <Box
      style={{
        backgroundColor: CCColors.Primary.Aqua,
        borderRadius: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        marginTop: 4,
      }}>
      <CCText fontSize={FontSizes[9]} fontWeight="bold" color={CCColors.White}>
        Current
      </CCText>
    </Box>
  );
  const upcomingTag = (
    <Box
      style={{
        backgroundColor: CCColors.Red,
        borderRadius: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        marginTop: 4,
      }}>
      <CCText fontSize={FontSizes[9]} fontWeight="bold" color={CCColors.White}>
        Upcoming
      </CCText>
    </Box>
  );

  // Find current and next level indices
  const currentIndex = displayLevels.findIndex(level => level.isCurrent);
  const hasNextLevel =
    displayLevels.length > 1 &&
    currentIndex >= 0 &&
    currentIndex < displayLevels.length - 1;

  useEffect(() => {
    // Reset animations
    progressAnim.setValue(0);
    pulseAnim.setValue(1);

    // Animate progress line from current to next level
    if (hasNextLevel && lineWidth.current > 0) {
      Animated.parallel([
        Animated.timing(progressAnim, {
          toValue: lineWidth.current,
          duration: 1500,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        }),
        // Pulse animation for current level
        Animated.loop(
          Animated.sequence([
            Animated.timing(pulseAnim, {
              toValue: 1.15,
              duration: 1000,
              easing: Easing.inOut(Easing.ease),
              useNativeDriver: true,
            }),
            Animated.timing(pulseAnim, {
              toValue: 1,
              duration: 1000,
              easing: Easing.inOut(Easing.ease),
              useNativeDriver: true,
            }),
          ]),
        ),
      ]).start();
    } else if (hasNextLevel) {
      // If line width not measured yet, just start pulse animation
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.15,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      ).start();
    } else {
      // Just pulse if no next level
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.15,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      ).start();
    }
  }, [
    displayLevels.length,
    currentIndex,
    progressAnim,
    pulseAnim,
    hasNextLevel,
  ]);

  // Handle line container layout to get actual width
  const handleLineLayout = (event: any) => {
    const {width} = event.nativeEvent.layout;
    if (width > 0 && width !== lineWidth.current) {
      lineWidth.current = width;
      // Restart animation with correct width
      progressAnim.setValue(0);
      Animated.timing(progressAnim, {
        toValue: width,
        duration: 1500,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    }
  };

  // Return null if current and upcoming levels have the same name
  if (
    currentIndex >= 0 &&
    currentIndex < displayLevels.length - 1 &&
    displayLevels[currentIndex].name === displayLevels[currentIndex + 1].name
  ) {
    return null;
  }

  return (
    <Box>
      <Box style={{paddingVertical: 10, position: 'relative', width: isMobileOrTab ? '90%' : '50%',margin:'auto'}}  >
        {/* Lines Layer - behind circles */}
        {displayLevels.length > 1 && (
          <View
            onLayout={handleLineLayout}
            style={{
              position: 'absolute',
              top: 21,
              left: 60,
              right: 60,
              height: 2,
              overflow: 'hidden',
            }}>
            <View
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: 2,
                backgroundColor: CCColors.Grey.Shade100,
              }}
            />
            {hasNextLevel && (
              <Animated.View
                style={{
                  position: 'absolute',
                  left: 0,
                  width: progressAnim,
                  height: 2,
                  backgroundColor: CCColors.Orange,
                }}
              />
            )}
          </View>
        )}

        {/* Circles and Labels Layer - on top */}
        <Box
          flexDirection="row"
          alignItems="flex-start"
          justifyContent={
            displayLevels.length === 1 ? 'flex-start' : 'space-between'
          }>
          {displayLevels.map((level, index) => {
            const isCurrent = level.isCurrent;

            return (
              <Box
                key={level.name}
                alignItems="center"
                style={{
                  width: displayLevels.length === 1 ? 'auto' : 120,
                  // flex: displayLevels.length === 1 ? 0 : 1,
                }}>
                {isCurrent ? (
                  <Animated.View
                    style={{
                      transform: [{scale: pulseAnim}],
                    }}>
                    <Box
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 12,
                        backgroundColor: level.isLocked
                          ? CCColors.Grey.Divider200
                          : level.color,
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 8,
                        ...styles.currentCircle,
                      }}>
                      {level.isCompleted ? (
                        <MaterialCommunityIcons
                          name="check"
                          size={14}
                          color={CCColors.White}
                        />
                      ) : (
                        <View
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: CCColors.TextColor.Dark,
                          }}
                        />
                      )}
                    </Box>
                  </Animated.View>
                ) : (
                  <Box
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor: level.isLocked
                        ? CCColors.Grey.Divider200
                        : level.color,
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    {level.isCompleted ? (
                      <MaterialCommunityIcons
                        name="check"
                        size={14}
                        color={CCColors.White}
                      />
                    ) : (
                      <View
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: CCColors.White,
                        }}
                      />
                    )}
                  </Box>
                )}

                <CCText
                  fontSize={FontSizes[12]}
                  color={
                    level.isLocked
                      ? CCColors.Grey.Grey
                      : CCColors.TextColor.Dark
                  }
                  textAlign="center"
                  fontWeight={level.isCurrent ? 'bold' : 'semibold'}
                  numberOfLines={1}
                  style={{maxWidth: displayLevels.length === 1 ? 120 : 120}}>
                  {level.name}
                </CCText>

                {level.isCurrent && currentTag}
                {level.order > currentIndex && upcomingTag}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  currentCircle: {
    shadowColor: CCColors.Primary.Default,
    shadowOffset: {width: 0, height: 0},
    shadowOpacity: 0.4,
    shadowRadius: 6,
    elevation: 4,
  },
});

export default LevelTracker;
